import { KeyRound, Plus } from 'lucide-react';
import { useState } from 'react';
import { PageError, PageLoading } from '../../../components';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { BotaoCopiar } from '../components/copiar';
import type { ApiCredencial, CredencialComSecret } from './credenciais-service';
import { mensagemErro, useCredenciais, useCredenciaisMutations } from './use-credenciais';

const dataHora = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—';

type Confirmacao = { tipo: 'rotacionar' | 'revogar'; credencial: ApiCredencial };

export function Credenciais() {
  const { data, isLoading, error } = useCredenciais();
  const { criar, rotacionar, revogar } = useCredenciaisMutations();
  const [novaAberta, setNovaAberta] = useState(false);
  const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);
  const [secretGerado, setSecretGerado] = useState<CredencialComSecret | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const confirmar = async () => {
    if (!confirmacao) return;
    setErro(null);
    try {
      if (confirmacao.tipo === 'rotacionar') {
        setSecretGerado(await rotacionar.mutateAsync(confirmacao.credencial.clientId));
      } else {
        await revogar.mutateAsync(confirmacao.credencial.clientId);
      }
    } catch (e) {
      setErro(mensagemErro(e));
    }
  };

  if (isLoading) return <PageLoading loading message="Carregando credenciais..." />;
  if (error) return <PageError error={mensagemErro(error)} />;

  const credenciais = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-sm text-[hsl(215,16%,47%)]">
          Cada sistema que acessa a API usa uma credencial (<code>client_id</code> e{' '}
          <code>client_secret</code>), trocada por um token de acesso de 30 minutos. Crie
          uma por sistema para poder revogar uma sem afetar as outras.
        </p>
        <Button onClick={() => setNovaAberta(true)} className="shrink-0 gap-2">
          <Plus className="h-4 w-4" />
          Nova credencial
        </Button>
      </div>

      {erro && <p className="text-sm text-red-600">{erro}</p>}

      {credenciais.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-[hsl(214,32%,91%)] p-10 text-center text-sm text-[hsl(215,16%,47%)]">
          <KeyRound className="h-6 w-6" />
          Nenhuma credencial criada.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Client ID</TableHead>
              <TableHead>Secret</TableHead>
              <TableHead>Criada em</TableHead>
              <TableHead>Último token emitido</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {credenciais.map((c) => (
              <TableRow key={c.clientId}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell>
                  <span className="font-mono text-xs">{c.clientId}</span>
                  <BotaoCopiar texto={c.clientId} rotulo="Copiar client_id" className="ml-1" />
                </TableCell>
                <TableCell className="font-mono text-xs">••••{c.secretFinal}</TableCell>
                <TableCell>{dataHora(c.criadaEm)}</TableCell>
                <TableCell>{dataHora(c.ultimoTokenEm)}</TableCell>
                <TableCell>
                  {c.status === 'ativa' ? (
                    <Badge variant="secondary">Ativa</Badge>
                  ) : (
                    <Badge variant="outline">Revogada</Badge>
                  )}
                </TableCell>
                <TableCell className="space-x-2 text-right">
                  {c.status === 'ativa' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmacao({ tipo: 'rotacionar', credencial: c })}
                      >
                        Gerar novo secret
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmacao({ tipo: 'revogar', credencial: c })}
                      >
                        Revogar
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <NovaCredencialDialog
        aberto={novaAberta}
        onClose={() => setNovaAberta(false)}
        onCriar={async (nome) => {
          const res = await criar.mutateAsync(nome);
          setNovaAberta(false);
          setSecretGerado(res);
        }}
        pendente={criar.isPending}
      />

      <ConfirmDialog
        open={!!confirmacao}
        onOpenChange={(open) => !open && setConfirmacao(null)}
        onConfirm={confirmar}
        title={
          confirmacao?.tipo === 'revogar'
            ? `Revogar "${confirmacao.credencial.nome}"?`
            : `Gerar novo secret para "${confirmacao?.credencial.nome}"?`
        }
        description={
          confirmacao?.tipo === 'revogar'
            ? 'Os sistemas que usam esta credencial perdem o acesso à API na hora. Não dá para desfazer.'
            : 'O secret atual e os tokens emitidos com ele deixam de funcionar na hora. Atualize os sistemas que usam esta credencial com o novo secret.'
        }
        confirmText={confirmacao?.tipo === 'revogar' ? 'Revogar' : 'Gerar novo secret'}
        cancelText="Cancelar"
      />

      <SecretDialog resultado={secretGerado} onClose={() => setSecretGerado(null)} />
    </div>
  );
}

function NovaCredencialDialog({
  aberto,
  onClose,
  onCriar,
  pendente,
}: {
  aberto: boolean;
  onClose: () => void;
  onCriar: (nome: string) => Promise<void>;
  pendente: boolean;
}) {
  const [nome, setNome] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const fechar = () => {
    setNome('');
    setErro(null);
    onClose();
  };

  const criar = async () => {
    setErro(null);
    try {
      await onCriar(nome);
      setNome('');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(open) => !open && fechar()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nova credencial</DialogTitle>
          <DialogDescription>
            Dê um nome que identifique o sistema que vai usar a credencial.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="credencial-nome">Nome</Label>
          <Input
            id="credencial-nome"
            value={nome}
            maxLength={60}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: ERP financeiro"
          />
          {erro && <p className="text-sm text-red-600">{erro}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={fechar} disabled={pendente}>
            Cancelar
          </Button>
          <Button onClick={criar} disabled={!nome.trim() || pendente}>
            {pendente ? 'Criando...' : 'Criar credencial'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// O secret só é mostrado aqui, uma vez.
function SecretDialog({
  resultado,
  onClose,
}: {
  resultado: CredencialComSecret | null;
  onClose: () => void;
}) {
  if (!resultado) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Credencial "{resultado.credencial.nome}"</DialogTitle>
          <DialogDescription>
            Copie o client_secret agora: ele não será mostrado de novo. Se perder, gere um
            novo secret.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Campo rotulo="client_id" valor={resultado.credencial.clientId} />
          <Campo rotulo="client_secret" valor={resultado.clientSecret} />
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Já guardei o secret</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[hsl(215,16%,47%)]">{rotulo}</span>
        <BotaoCopiar texto={valor} rotulo={`Copiar ${rotulo}`} />
      </div>
      <code
        data-testid={rotulo}
        className="block break-all rounded border border-[hsl(214,32%,91%)] bg-[hsl(210,40%,98%)] p-2 text-xs"
      >
        {valor}
      </code>
    </div>
  );
}
